import { useCallback } from 'react';
import {
  CUSTOM_DESCRIPTION_PREFIX,
  isCustomDescription,
  parseCustomDescription,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/rfqs-functions';
import { lima, razzmatazz } from '@paradigm/design-system/src/colors';
import ErrorBoundary from '../../../../ErrorBoundary';

interface Props {
  readonly children: string;
  readonly className?: string;
}

export default function ColoredStrategyDescription(props: Props) {
  const { children, className } = props;

  if (isCustomDescription(children))
    return (
      <CustomStrategyDescription className={className}>
        {children}
      </CustomStrategyDescription>
    );

  return (
    <span className={className} css="white-space: pre-line;">
      {children}
    </span>
  );
}

function CustomStrategyDescription({ children, className }: Props) {
  const legs = parseCustomDescription(children);

  return legs.length > 3 ? (
    <ReferToTable className={className} />
  ) : (
    <CustomLegsDescription legs={legs} className={className} />
  );
}

function ReferToTable({ className }: { className?: string }) {
  return (
    <div data-testid="refer-to-table" className={className}>
      <div>{CUSTOM_DESCRIPTION_PREFIX}</div>
      <div css="padding-left: 2ch;">See table for details</div>
    </div>
  );
}

function CustomLegsDescription({
  legs,
  className,
}: {
  legs: readonly string[];
  className?: string;
}) {
  const getLegDesc = useCallback(
    () => legs.map((leg) => <LegDescription key={leg} text={leg} />),
    [legs],
  );

  return (
    <ErrorBoundary fallback={<ReferToTable className={className} />}>
      <div css="display: flex;" className={className}>
        <div css="flex-basis: 0;">{CUSTOM_DESCRIPTION_PREFIX}&nbsp;&nbsp;</div>
        <div>{getLegDesc()}</div>
      </div>
    </ErrorBoundary>
  );
}

interface LegDescriptionProps {
  readonly text: string;
  readonly suffix?: string;
}

const makeRatioCss = (sign: string) =>
  sign === '+' ? `color: ${lima};` : `color: ${razzmatazz};`;

function LegDescription({ text, suffix }: LegDescriptionProps) {
  const { sign, ratio, instrument } = parseLegDescription(text);

  return (
    <div data-testid="leg-description">
      <span css={makeRatioCss(sign)}>
        {sign}
        {ratio}
      </span>{' '}
      <span>{instrument}</span>
      <span>{suffix}</span>
    </div>
  );
}

interface LegDescriptor {
  readonly sign: string;
  readonly ratio: string;
  readonly instrument: string;
}

const LEG_PARSE_RE = /^([+-])(\d+\.?\d*…*)\s+(.*?)$/u;
/**
 * For now we are keeping this, but in the future we should get the
 * leg details from the legs object to avoid errors caused by RegExp.
 */
function parseLegDescription(text: string): LegDescriptor {
  const match = LEG_PARSE_RE.exec(text);
  if (match == null)
    throw new TypeError(`Could not parse leg description "${text}"`);
  const [, sign = '', ratio = '', instrument = ''] = match;

  return { sign, ratio, instrument };
}
